import { Children, useMemo } from "react"
import { useLocation, useNavigate } from "react-router-dom"

import type { Route } from "../../config/config-utils"
import type { PanelProps } from "./panel-props"

import {
  MAIN_APPLICATIONS_ROUTES_CONFIG_SOURCE,
  SECONDARY_APPLICATIONS_ROUTES_CONFIG_SOURCE,
} from "../../config/config"
import { useIsMobile } from "../../hooks/is-mobile"
import {
  type ThemeMode,
  useDarkMode,
} from "../../hooks/use-dark-mode"
import { IS_AUTH } from "../../modules/auth"
import { requestTourStart } from "../../modules/tour-start-store"
import { Button } from "../button"
import { Select } from "../select"
import { Row } from "./row.tsx"
import { switchApplication } from "./switch-application.ts"

const THEME_OPTIONS: { key: ThemeMode; label: string }[] = [
  { key: "light", label: "Light" },
  { key: "dark", label: "Dark" },
  { key: "system", label: "System" },
]

export function SharedSettings(
  input: PanelProps & { children?: React.ReactNode },
) {
  const navigate = useNavigate()
  const { setThemeMode, themeMode } = useDarkMode()
  const location = useLocation()

  const isMobile = useIsMobile()

  const currentAppOptions = useMemo(()=> {
    const main = MAIN_APPLICATIONS_ROUTES_CONFIG_SOURCE.filter(
      (x): x is Route=> x !== null,
    )
    return isMobile ? main.filter(x=> x.mobile !== false) : main
  }, [isMobile])
  const secondaryOptions = SECONDARY_APPLICATIONS_ROUTES_CONFIG_SOURCE.filter(
    (x): x is Route=> x !== null,
  )
  const allRoutes = [...currentAppOptions, ...secondaryOptions]
  const currentAppRoute = allRoutes.find(x=> x.route === location.pathname)

  const hasChildren = Children.count(input.children) > 0
  return (
    <div className="sidebar__container">
      <section className="sidebar__section">
        <h3 className="sidebar__section-title">Appearance</h3>
        <Row>
          <Select
            initialValue={themeMode}
            label="Theme"
            onChange={(value)=> setThemeMode(value as ThemeMode)}
            options={THEME_OPTIONS}
          />
        </Row>
      </section>

      <div className="sidebar__separator" role="separator" />

      <section className="sidebar__section">
        <h3 className="sidebar__section-title">Application</h3>
        <Row>
          <Select
            initialValue={currentAppRoute?.key ?? ""}
            label={"Application"}
            moreOptions={secondaryOptions}
            moreOptionsIsActive={secondaryOptions.some(
              x=> x.route === location.pathname,
            )}
            onChange={switchApplication({ navigate })}
            options={currentAppOptions}
          />
        </Row>
      </section>

      {hasChildren && <div className="sidebar__separator" role="separator" />}

      {Children.toArray(input.children).map((child, index)=> (
        <Row key={index}>{child}</Row>
      ))}

      {/* Tours are a non-auth feature: the generic shell guide is available on
          every page and the game tours on their own routes — so the button
          renders for all guests and each subscriber picks it up. */}
      {!IS_AUTH && (
        <>
          <div className="sidebar__separator" role="separator" />
          <section className="sidebar__section">
            <h3 className="sidebar__section-title">Tour</h3>
            <Row>
              <Button
                onClick={()=> {
                  // Close the sidebar so the started tour is visible over the
                  // game — it must not run underneath the open sidebar (and,
                  // on mobile, fullscreen is restored).
                  input.closeSidebar()
                  requestTourStart()
                }}
                type="button"
              >
                Start Tour
              </Button>
            </Row>
          </section>
        </>
      )}
    </div>
  )
}
